import { useMemo } from 'react'
import { useMyCohort, useMyPurchases, type PlatformRole } from '@lb/domain'

import { http } from '../../lib/client'
import { useSessionContext } from '../providers/session'
import { PATH_FOR_SURFACE, lockedNavPaths } from './nav'

const NONE: ReadonlySet<string> = new Set()

/**
 * The nav paths this member cannot open yet — driven by `journey.lockedSurfaces`
 * on `GET /cohorts/mine`, the read the TSP shell already makes.
 *
 * Only TSP asks: the locks are a cohort member's, and every other role has no
 * cohort to be locked by. The day the étape starts the server stops listing
 * the surface, and the next read (focus, or the minute-long staleness) brings
 * the item back — no client clock, and no release.
 *
 * While the read is in flight the lockable items stay hidden, so a locked
 * member is never shown a door that then vanishes; a failed read hides
 * nothing, and the route's own `SURFACE_LOCKED` answer still holds the line.
 */
export function useHiddenNavPaths(role: PlatformRole): ReadonlySet<string> {
  const tsp = role === 'TSP'
  const cohort = useMyCohort(http, { enabled: tsp })

  return useMemo(() => {
    if (!tsp) return NONE
    if (cohort.isPending) return new Set(Object.values(PATH_FOR_SURFACE))
    const locks = cohort.data?.lockedSurfaces ?? []
    const paths = locks
      .map((lock) => PATH_FOR_SURFACE[lock.surface])
      .filter((path): path is string => path !== undefined)
    return paths.length > 0 ? new Set(paths) : NONE
  }, [tsp, cohort.isPending, cohort.data])
}

/**
 * The nav paths a **single-purchase member** cannot open (13 Oct 2026).
 *
 * The cohort is exactly the one `AccessCard` already draws for: not entitled,
 * and holding at least one purchase. A lapsed member who owns **nothing** is
 * deliberately left alone — they have bought nothing that the nav could be
 * telling them about, and changing their rail is a different decision from
 * the one taken here.
 *
 * Nothing is locked while the read is in flight: a rail that fills in with
 * padlocks a second after it draws is worse than one that never had them, and
 * the read is the same query the Home strip and the rail card already make.
 */
export function useLockedNavPaths(role: PlatformRole): ReadonlySet<string> {
  const { viewer } = useSessionContext()
  const entitled = viewer?.entitlement === 'entitled'
  const purchases = useMyPurchases(http, Boolean(viewer) && !entitled)
  const items = purchases.data?.items

  return useMemo(() => {
    if (entitled || !items || items.length === 0) return NONE
    const owns = new Set<'COURSE' | 'EVENT'>()
    for (const item of items) {
      if (item.contentType === 'COURSE' || item.contentType === 'EVENT') owns.add(item.contentType)
    }
    return lockedNavPaths(role, owns)
  }, [role, entitled, items])
}
